import { useMemo } from 'react';
import Skeleton from '@mui/material/Skeleton';
// CircleAlert, ExternalLink and SEVERITY_COLORS go back in these imports when the Known exploit card is restored.
import { Activity, Waypoints, Wrench } from 'lucide-react';
import agent from '@root/agent';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import { CATEGORICAL_COLORS } from '@root/utilities/pastelColors';
import DetailSection from './DetailSection';
import FindingPathGraph from './FindingPathGraph';
import FixAvailable from './FixAvailable';
import { toFindingPath } from './findingGraphMapper';
import ResourceRisks from './ResourceRisks';

// "Overview" tab of the Findings side overlay: the path from the workload to the CVE, then what makes
// the resource risky, how to fix it and whether it is exploited.

const FixSkeleton = () => (
  <>
    <Skeleton variant="text" width="100%" height={26} />
    <Skeleton variant="text" width="70%" height={26} />
    <Skeleton variant="rounded" width="100%" height={36} />
  </>
);

// Known exploit card: hidden for now (it only has mock data, see ./mockData).
// const KnownExploit = ({ exploit }) => (
//   <>
//     <dl className="FindingDetails__FactGrid">
//       <dt>Exploit maturity</dt>
//       <dd>{exploit.maturity}</dd>
//       <dt>CISA KEV</dt>
//       <dd>{exploit.cisaKev ? 'Listed' : 'Not listed'}</dd>
//       <dt>EPSS score</dt>
//       <dd>
//         {(exploit.epssScore * 100).toFixed(2)}%{' '}
//         <span className="FindingDetails__Muted">(higher than {Math.round(exploit.epssPercentile * 100)}% of CVEs)</span>
//       </dd>
//       <dt>Published</dt>
//       <dd>{exploit.publishedAt}</dd>
//     </dl>
//     <ul className="FindingDetails__References">
//       {exploit.references.map(reference => (
//         <li key={reference.url}>
//           <a href={reference.url} target="_blank" rel="noopener noreferrer">
//             {reference.label}
//             <ExternalLink size={12} aria-hidden="true" />
//           </a>
//         </li>
//       ))}
//     </ul>
//   </>
// );

const OverviewTab = ({ selectedRow }) => {
  const { data: findingDetailsData, isFetching: isFetchingFindingDetails } = useEnhancedQuery(
    ['get_vulnerabilities_findings_details', selectedRow?.account_id, selectedRow?._id],
    () =>
      agent.Kubernetes.get_vulnerabilities_findings_details({
        environment: selectedRow?.account_id,
        finding_id: selectedRow?._id,
      }),
    { enabled: Boolean(selectedRow?.account_id && selectedRow?._id) },
  );

  const findingDetails = findingDetailsData?.data;
  const { path, severity } = useMemo(() => toFindingPath(findingDetails), [findingDetails]);
  const workload = findingDetails?.nodes?.find(node => node.type === 'workload')?.data;

  return (
    <div className="FindingDetails__Tab">
      <DetailSection title="Attack path" icon={Waypoints} iconColor={CATEGORICAL_COLORS.mutedBlue}>
        {isFetchingFindingDetails ? (
          <Skeleton variant="rounded" width="100%" height={280} />
        ) : path.length ? (
          <FindingPathGraph path={path} severity={severity} />
        ) : (
          <p className="FindingDetails__Paragraph FindingDetails__Muted">
            No attack path is available for this finding.
          </p>
        )}
      </DetailSection>

      <DetailSection title="Resource risk analysis" icon={Activity} iconColor={CATEGORICAL_COLORS.purple}>
        <ResourceRisks
          accountId={selectedRow?.account_id}
          workload={workload}
          isLoadingWorkload={isFetchingFindingDetails}
        />
      </DetailSection>

      <DetailSection title="Fix available" icon={Wrench} iconColor={CATEGORICAL_COLORS.amber}>
        {isFetchingFindingDetails ? <FixSkeleton /> : <FixAvailable fix={findingDetails?.fix} />}
      </DetailSection>

      {/* Restoring it also needs `details` back in this component's props (index passes it).
      <DetailSection title="Known exploit" icon={CircleAlert} iconColor={SEVERITY_COLORS.critical}>
        <KnownExploit exploit={details.exploit} />
      </DetailSection> */}
    </div>
  );
};

export default OverviewTab;
